import { useEffect, useRef, useState } from "react"
import Navbar from "../components/Navbar"
import GetInTouch from "../components/GetInTouch"
import Footer from "../components/Footer"
import heroBg from "../assets/hero-bg.png"
import petrol from "../assets/petrol.png"
import improvedEfficiencyImg from "../assets/ChatGPT Image Jun 9, 2026, 11_12_07 AM.png"
import enhancedSecurityImg from "../assets/ChatGPT Image Jun 9, 2026, 11_11_45 AM (2).png"
import customerSatisfactionImg from "../assets/ChatGPT Image Jun 9, 2026, 11_11_45 AM (3).png"
import KeyFeatures from "../components/KeyFeatures"
import CtaSection from "../components/CtaSection"
import iot from "../assets/iot.png"


const consignmentKeyFeatures = [
  {
    title: "On-Time Delivery Assurance",
    desc: "Ensure timely deliveries by proactively managing potential disruptions and optimizing logistics operations.",
  },
  {
    title: "Geo-Fencing",
    desc: "Define virtual boundaries for your consignments and receive instant alerts if a shipment deviates from its designated route.",
  },
  {
    title: "Route Optimization",
    desc: "Leverage intelligent algorithms to determine the most efficient routes, reducing transit time, fuel consumption, and operational costs.",
  },
  {
    title: "SMS & Email Alerts",
    desc: "Stay updated with automated notifications for delays, route deviations, delivery confirmations, and other critical events.",
  },
  {
    title: "Driver & Asset Safety",
    desc: "Enhance driver and asset safety with remote immobilization, emergency alerts, and real-time security monitoring.",
  },
  {
    title: "Detailed Analytics Reports",
    desc: "Gain insights into logistics operations with reports that help analyze performance and identify areas for improvement.",
  },
  {
    title: "Web & Mobile Platform",
    desc: "Access the platform through web browsers or mobile devices to manage and monitor consignments anytime, anywhere.",
  },
  {
    title: "Paperless Documentation",
    desc: "Transition to a digital workflow with electronic documentation, reducing paperwork and streamlining administrative processes.",
  },
  {
    title: "Real-Time Visibility",
    desc: "Monitor consignments in real time across road, rail, or other transport modes with exact location and status updates.",
  },
]

const industries = [
  {
    icon: "truck",
    title: "Multi-Modal Transporters",
    desc: "Seamlessly track consignments across various transportation modes including road, rail, air, and sea.",
  },
  {
    icon: "corporate",
    title: "E-Commerce & Retail Logistics",
    desc: "Ensure last-mile visibility and improve customer satisfaction by tracking orders in real time, managing delivery windows, and handling returns efficiently.",
  },
  {
    icon: "truck",
    title: "Fleet Operators",
    desc: "Enhance fleet efficiency and reduce operational costs with real-time tracking and optimized route planning.",
  },
  {
    icon: "institution",
    title: "Shippers & Manufacturers",
    desc: "Monitor shipments to ensure product integrity and timely delivery to customers and distributors.",
  },
  {
    icon: "taxi",
    title: "Freight Forwarders",
    desc: "Gain real-time insights to manage complex logistics networks effectively across multiple transportation modes.",
  },
  {
    icon: "corporate",
    title: "3PL Companies",
    desc: "Offer clients transparency and reliability in shipment handling with advanced tracking capabilities.",
  },
]

const scrollToAbout = () => {
  document
    .getElementById("about-ais140")
    ?.scrollIntoView({ behavior: "smooth" });
};

const BusIcon = () => (
    <svg
        className="mx-auto mb-4 h-10 w-10 text-[#ed860f] sm:h-11 sm:w-11"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="10" y="14" width="44" height="30" rx="5" stroke="currentColor" strokeWidth="4" />
        <path d="M14 26H50" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <path d="M24 14V26" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <path d="M40 14V26" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <path d="M18 36H26" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <path d="M38 36H46" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <circle cx="22" cy="47" r="4" stroke="currentColor" strokeWidth="4" />
        <circle cx="42" cy="47" r="4" stroke="currentColor" strokeWidth="4" />
    </svg>
)

const RefreshIcon = () => (
    <svg className="h-10 w-10 text-white" viewBox="0 0 24 24" fill="none">
        <path
            d="M20 12a8 8 0 0 1-14.8 4.2M4 12A8 8 0 0 1 18.8 7.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <path d="M5 17H2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M19 7h3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const whyChooseCards = [
  {
    type: "efficiency",
    title: "Improved Efficiency",
    desc: "Optimize routes and reduce operational costs with real-time tracking and data-driven insights.",
    image: improvedEfficiencyImg,
  },
  {
    type: "security",
    title: "Enhanced Security",
    desc: "Protect your assets and shipments with advanced security features including geo-fencing and remote immobilization.",
    image: enhancedSecurityImg,
  },
  {
    type: "customers",
    title: "Customer Satisfaction",
    desc: "Increase customer satisfaction with timely deliveries and transparent shipment tracking information.",
    image: customerSatisfactionImg,
  },
]

const ScrollRevealCard = ({ children, delay = 0 }) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const current = ref.current
    if (!current) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.unobserve(current)
        }
      },
      {
        threshold: 0.22,
        rootMargin: "0px 0px -70px 0px",
      }
    )

    observer.observe(current)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? "translate-y-0 opacity-100" : "translate-y-14 opacity-0"
      }`}
    >
      {children}
    </div>
  )
}

const WhyChooseIcon = ({ type }) => {
  const iconClass = "h-8 w-8 text-[#ed860f]"

  const icons = {
    efficiency: (
      <svg className={iconClass} viewBox="0 0 32 32" fill="none">
        <path
          d="M6 20a10 10 0 1 1 20 0"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M16 20l5-7"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M9 20h14"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="16" cy="20" r="2" fill="currentColor" />
      </svg>
    ),
    security: (
      <svg className={iconClass} viewBox="0 0 32 32" fill="none">
        <rect
          x="8"
          y="14"
          width="16"
          height="12"
          rx="2"
          fill="currentColor"
        />
        <path
          d="M11 14V10a5 5 0 0 1 10 0v4"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    customers: (
      <svg className={iconClass} viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="10" r="4" fill="currentColor" />
        <circle cx="8" cy="13" r="3" fill="currentColor" opacity="0.85" />
        <circle cx="24" cy="13" r="3" fill="currentColor" opacity="0.85" />
        <path
          d="M8 24c1.2-4 4-6 8-6s6.8 2 8 6"
          fill="currentColor"
        />
        <path
          d="M3 24c.8-3 2.8-4.8 5.8-5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <path
          d="M29 24c-.8-3-2.8-4.8-5.8-5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
    ),
  }

  return (
    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#edf2fb]">
      {icons[type] || icons.efficiency}
    </div>
  )
}

export default function Section2() {


    const containerClass =
        "mx-auto w-full max-w-[1600px] px-5 sm:px-8 lg:px-12 xl:px-20"

const whyChooseSectionRef = useRef(null)
const whyChooseCardRefs = useRef([])
const [activeWhyChoose, setActiveWhyChoose] = useState(0)

useEffect(() => {
  let ticking = false

  const updateActiveCard = () => {
    const checkpoint = window.innerHeight * 0.45
    let nextActive = 0

    whyChooseCardRefs.current.forEach((card, index) => {
      if (!card) return

      const rect = card.getBoundingClientRect()

      if (rect.top <= checkpoint) {
        nextActive = index
      }
    })

    setActiveWhyChoose(nextActive)
    ticking = false
  }

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateActiveCard)
      ticking = true
    }
  }

  updateActiveCard()
  window.addEventListener("scroll", onScroll, { passive: true })
  window.addEventListener("resize", onScroll)

  return () => {
    window.removeEventListener("scroll", onScroll)
    window.removeEventListener("resize", onScroll)
  }
}, [])

const trackingTechnologyItems = [
  {
    title: "GPS & Cellular Connectivity",
    desc: "Multi-network capabilities ensure consistent connectivity even in remote areas.",
    icon: "gps",
  },
  {
    title: "IoT Sensors",
    desc: "Advanced sensors for monitoring temperature, humidity, shock, and other critical parameters.",
    icon: "sensor",
  },
  {
    title: "Cloud Infrastructure",
    desc: "Scalable cloud-based platform with 99.9% uptime guarantee for reliable operation.",
    icon: "cloud",
  },
]

    return (
        <>
            {/* Hero */}
            <section className="relative mx-auto w-full max-w-[calc(100%-24px)] overflow-hidden rounded-b-[42px] bg-black sm:max-w-[calc(100%-32px)] lg:max-w-[calc(100%-48px)]">
                <Navbar />

                <div
                    className="relative min-h-[560px] bg-cover bg-center sm:min-h-[620px] lg:min-h-[700px]"
                    style={{ backgroundImage: `url(${heroBg})` }}
                >
                    <div className="absolute inset-0 bg-black/65" />

                    <div className="relative z-10 flex min-h-[560px] items-center justify-center px-5 pt-24 text-center sm:min-h-[620px] lg:min-h-[700px]">
                        <div className="mx-auto max-w-[900px]">
                            <h1 className="text-[34px] font-bold leading-tight text-white sm:text-5xl lg:text-[60px]">
                                Smart Consignment <br className="hidden sm:block" />
                                Tracking Solution
                            </h1>

                            <div className="mx-auto mt-3 h-[3px] w-[240px] rounded-full bg-[#ed860f] sm:w-[420px] lg:w-[520px]" />

                            <p className="mx-auto mt-7 max-w-[760px] text-sm leading-relaxed text-white/75 sm:text-base">
                                In today’s dynamic supply chain environment, ensuring the safety
                                and timely delivery of consignments is paramount. Our Smart
                                Consignment Tracking Solution offers real-time monitoring and
                                management of shipments across various transportation modes,
                                providing unparalleled visibility and control.
                            </p>

                            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                             <button
                                 onClick={scrollToAbout}
                                    href="/services"
                                    className="inline-flex min-w-[210px] items-center justify-center rounded-md bg-[#ed860f] px-8 py-4 text-sm font-medium text-white transition hover:bg-[#d9780d]"
                                >
                                    Explore Solutions
                                </button>

                                <a
                                    href="/contact"
                                    className="inline-flex min-w-[210px] items-center justify-center rounded-md border border-white px-8 py-4 text-sm font-medium text-white transition hover:bg-white hover:text-black"
                                >
                                    Contact us
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Features */}
        <KeyFeatures features={consignmentKeyFeatures} />

            {/* Industries */}
            <section 
              id="about-ais140"
              className="bg-white py-16 sm:py-20 lg:py-24">
                <div className={`${containerClass} text-center`}>
                    <h2 className="mx-auto mb-12 max-w-[900px] text-3xl font-bold leading-tight text-black sm:text-4xl lg:mb-16 lg:text-[42px]">
                        Industries Benefiting from Our Solution
                    </h2>

                    <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-y-16">
                        {industries.map((item, index) => (
                            <div key={index} className="mx-auto max-w-[310px] text-center">
                                <BusIcon />

                                <h3 className="mb-3 text-lg font-bold leading-tight text-black sm:text-xl">
                                    {item.title}
                                </h3>

                                <p className="mx-auto max-w-[260px] text-sm leading-relaxed text-gray-600 sm:text-[15px]">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

     {/* Why Choose */}
<section
  ref={whyChooseSectionRef}
  className="relative bg-white py-16 sm:py-20 lg:py-24"
>
  <div className={containerClass}>
    <div className="mb-12 text-center">
      <h2 className="text-3xl font-bold text-black sm:text-4xl lg:text-[42px]">
        Why Choose TrackSmart?
      </h2>

      <p className="mx-auto mt-3 max-w-[760px] text-sm leading-relaxed text-gray-600 sm:text-[15px]">
        Our solution offers unique advantages to help you optimize your
        logistics operations.
      </p>
    </div>

    <div className="relative mx-auto max-w-[1280px]">
      {/* working left indicator */}
      <div className="absolute left-[-38px] top-0 hidden h-full lg:block">
        <div className="sticky top-[260px] h-[165px] w-3 rounded-full bg-[#f5e9e9]">
          <div
            className="absolute left-1/2 h-9 w-2 -translate-x-1/2 rounded-full bg-[#ed860f] transition-all duration-500 ease-out"
            style={{
              top: `${24 + activeWhyChoose * 48}px`,
            }}
          />
        </div>
      </div>

      <div className="space-y-10">
        {whyChooseCards.map((item, index) => (
          <div
            key={item.title}
            ref={(el) => {
              whyChooseCardRefs.current[index] = el
            }}
            className="lg:sticky"
            style={{
              top: `${110 + index * 18}px`,
              zIndex: index + 1,
            }}
          >
            <div className="flex flex-col overflow-hidden rounded-xl bg-[#ed860f] p-2.5 shadow-[0_20px_45px_rgba(237,134,15,0.22)] transition-all duration-500 sm:p-3 lg:h-[315px] lg:flex-row">
              <div className="w-full lg:w-[500px]">
                <img
  src={item.image}
  alt={item.title}
  className="h-[250px] w-full rounded-lg object-cover sm:h-[300px] lg:h-full"
/>
              </div>

              <div className="flex flex-1 items-center px-7 py-8 text-white sm:px-10 lg:px-16 lg:py-0">
                <div>
                  <RefreshIcon />

                  <h3 className="mt-5 text-2xl font-bold lg:text-[26px]">
                    {item.title}
                  </h3>

                  <p className="mt-4 max-w-[560px] text-sm leading-relaxed text-white/90 sm:text-[15px]">
                    {item.desc}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>

            {/* Advanced Tracking Infrastructure */}
<section className="bg-white py-14 sm:py-16 lg:py-20">
  <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8">
    <div className="grid items-center gap-12 lg:grid-cols-[1fr_520px] xl:gap-16">
      <div>
        <h2 className="text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-[42px]">
          Advanced Tracking Infrastructure
        </h2>

        <p className="mt-5 max-w-[700px] text-sm leading-[1.8] text-gray-700 sm:text-[15px]">
          Our solution utilizes state-of-the-art GPS tracking devices, IoT
          sensors, and cloud-based infrastructure to provide reliable and
          accurate tracking information in real-time.
        </p>

        <div className="mt-10 space-y-9">
          {trackingTechnologyItems.map((item) => (
            <div key={item.title} className="flex gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#edf2fb] text-[#ed860f]">
                {item.icon === "gps" ? (
                  <svg
                    viewBox="0 0 24 24"
                    className="h-6 w-6"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 3v3M12 18v3M3 12h3M18 12h3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8 8l8 8M16 8l-8 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : item.icon === "sensor" ? (
                  <svg
                    viewBox="0 0 24 24"
                    className="h-6 w-6"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="7"
                      y="7"
                      width="10"
                      height="10"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M4 9h3M4 15h3M17 9h3M17 15h3M9 4v3M15 4v3M9 17v3M15 17v3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    className="h-6 w-6"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M7 18h10a4 4 0 0 0 .5-8A6 6 0 0 0 6.2 8.4 4.8 4.8 0 0 0 7 18Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </div>

              <div>
                <h3 className="text-xl font-bold leading-tight text-black">
                  {item.title}
                </h3>

                <p className="mt-3 max-w-[620px] text-sm leading-[1.75] text-gray-700 sm:text-[15px]">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)] sm:p-10">
        <img
          src={iot}
          alt="Advanced tracking infrastructure"
          className="mx-auto h-[320px] w-full object-contain sm:h-[380px] lg:h-[430px]"
        />
      </div>
    </div>
  </div>
</section>

            {/* CTA */}
            <CtaSection />

            <Footer />
        </>
    )
}